import Cookies from 'js-cookie';

export const COOKIE_NAME = 'login_personalization';

export interface PersonalizationRule {
    name?: string;
    value?: string;
    url?: string;
    setQuery?: Record<string, string>;
    data?: { slices?: { name?: string; pageDetailsA?: string }[]; [key: string]: any };
}

export interface PersonalizationMatch {
    item: PersonalizationRule;
    source: 'query' | 'referrer' | 'cookie';
    value: string;
}

const VALUES_SEPARATOR = '|';

// The cookie keeps every selected value (`SP|Corp`); a repeated value is not added twice.
export function addCookieValue(cookie: string | undefined, value: string): string {
    if (!cookie) return value;
    if (cookie.split(VALUES_SEPARATOR).includes(value)) return cookie;
    return `${cookie}${VALUES_SEPARATOR}${value}`;
}

// A rule matches the whole cookie value: `SP|Corp` needs its own rule with `value: "SP|Corp"`.
export function findPersonalizationCookie(rules: PersonalizationRule[] = []) {
    return rules.find((item) => {
        const value = Cookies.get(item.name || COOKIE_NAME);
        if (value === undefined) return false;
        return item.value === undefined || value === item.value;
    });
}

// Priority: `referrer` > `query` > `cookie`. A matched referrer rule writes its `setQuery` into the URL on mount,
// so checking the query first would report (and, on client navigation, switch) that rule as `query`.
// Returns `{ item, source, value }` of the first matching rule.
export function resolvePersonalizationRule(
    personalization: { referrer?: PersonalizationRule[]; cookie?: PersonalizationRule[] } = {},
    { query = {}, referrer = '' }: { query?: Record<string, any>; referrer?: string } = {},
): PersonalizationMatch | null {
    const list = personalization.referrer || [];

    const referrerItem = list.find((rule) => referrer.endsWith(rule.url));
    if (referrerItem) return { item: referrerItem, source: 'referrer', value: referrerItem.url };

    const queryItem = list.find((rule) => {
        if (!rule.setQuery) return false;
        const [key, value] = Object.entries(rule.setQuery)[0];
        return query[key] === value;
    });
    if (queryItem) {
        const [key, value] = Object.entries(queryItem.setQuery)[0];
        return { item: queryItem, source: 'query', value: `${key}=${value}` };
    }

    const cookieItem = findPersonalizationCookie(personalization.cookie || []);
    if (cookieItem) return { item: cookieItem, source: 'cookie', value: Cookies.get(cookieItem.name || COOKIE_NAME) };

    return null;
}
